import React, { useState } from 'react';
import axios from 'axios';
import { Building2 } from 'lucide-react';
import { useToast } from '@/context/ToastContext';
import VerificationGate from '@/components/verification/VerificationGate';
import { canDo } from '@/utils/verifications';
import { frenchCompanyNumber } from '@/utils/siret';
import '@/styles/become-company.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;

/** Encart du profil : un particulier déclare son entreprise ; l'admin valide ensuite le SIRET. */
export default function BecomeCompanyCard({ profile, onDone }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [siret, setSiret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!profile || profile.profile_type === 'PROFESSIONAL') return null;

  const fr = frenchCompanyNumber(siret);
  const badFrench = !!fr.kind && !fr.valid;
  let hint = 'SIRET (14 chiffres), SIREN (9 chiffres) ou numéro d\'immatriculation de votre pays (RCCM, NINEA…).';
  if (fr.kind && fr.valid) hint = `Format ${fr.kind} valide.`;
  else if (badFrench) hint = `Ce ${fr.kind} semble erroné (clé de contrôle invalide) : vérifiez le numéro.`;
  else if (siret.trim().length >= 5) hint = 'Numéro hors de France : notre équipe le vérifiera manuellement.';

  const submit = async (e) => {
    e.preventDefault();
    if (companyName.trim().length < 2) { setError('Indiquez le nom de votre société.'); return; }
    if (siret.trim().length < 5) { setError('Indiquez le SIRET ou le numéro d\'immatriculation.'); return; }
    if (badFrench) { setError('Le numéro saisi n\'est pas valide.'); return; }
    setError('');
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/users/me/company`, { company_name: companyName.trim(), siret: siret.trim() });
      toast.success('Demande envoyée : notre équipe vérifie votre SIRET.');
      onDone?.(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Impossible d\'envoyer la demande. Réessayez dans un instant.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card become-company" data-testid="become-company">
      <div className="become-company-hd">
        <Building2 size={20} aria-hidden="true" className="become-company-icon" />
        <div>
          <div className="become-company-title">Vous êtes une entreprise ?</div>
          <div className="become-company-text">
            Passez en compte Entreprise. Une fois votre SIRET vérifié par notre équipe, la mention « Entreprise »
            s'affiche sur vos annonces et vos colis peuvent aller jusqu'à 1 500 kg.
          </div>
        </div>
      </div>

      {profile.company_rejected_reason && (
        <div className="become-company-rejected" data-testid="become-company-rejected">
          Votre précédente demande a été refusée : {profile.company_rejected_reason}
        </div>
      )}

      {!canDo(profile, 'become_company') ? (
        <VerificationGate user={profile} action="become_company" variant="panel" />
      ) : !open ? (
        <button type="button" className="btn btn-ghost btn-sm become-company-open" onClick={() => setOpen(true)} data-testid="btn-become-company">
          Passer en compte Entreprise
        </button>
      ) : (
        <form className="become-company-form" onSubmit={submit} noValidate>
          <label className="become-company-label" htmlFor="bc-name">Nom de la société</label>
          <input id="bc-name" className="field-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)}
            autoComplete="organization" data-testid="input-company-name" />
          <label className="become-company-label" htmlFor="bc-siret">SIRET ou numéro d'immatriculation</label>
          <input id="bc-siret" className="field-input" value={siret} onChange={(e) => setSiret(e.target.value)}
            inputMode="text" data-testid="input-company-siret" />
          <div className={`become-company-hint${badFrench ? ' is-error' : fr.valid ? ' is-ok' : ''}`} data-testid="company-siret-hint">{hint}</div>
          {error && <div className="become-company-error" role="alert">{error}</div>}
          <div className="become-company-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setOpen(false); setError(''); }}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={busy} data-testid="btn-submit-company">
              {busy ? 'Envoi…' : 'Envoyer pour vérification'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
